import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Mic, MicOff, Send, X, Volume2, VolumeX, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { dishImg, onDishImgError } from "../lib/dishImage";

const SESSION_KEY = (slug) => `mnu_assistant_${slug}`;

const FALLBACK_SUGGESTIONS = [
  "I want a light vegetarian dinner for two",
  "Pick a fiery starter and a cooling drink",
  "What's good for a date night?",
  "Gluten-free options please",
  "I have 30 minutes — what's quick?",
];

function genSessionId() {
  if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * MenuAssistant — floating button + drawer with voice/text chat that
 * recommends dishes from the live menu. Voice via Web Speech API, TTS via
 * SpeechSynthesis. Multi-turn via session_id stored in localStorage.
 */
export default function MenuAssistant({ slug, restaurant, onAddItem }) {
  // AI Sommelier is Elite-plan only (Iter20). Keep "ultra" alias in the
  // check so legacy tenants whose tier-migration didn't run still work.
  const proEnabled = ["elite", "ultra"].includes((restaurant?.subscription_tier || "").toLowerCase()) && restaurant?.assistant_enabled !== false;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, recs?: [item]}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(() => {
    try { return localStorage.getItem("mnu_assistant_tts") !== "off"; } catch { return true; }
  });
  const ttsOnRef = useRef(ttsOn); // keep latest value for inside async closures
  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const [welcomeLoaded, setWelcomeLoaded] = useState(false);
  const [defaultChips, setDefaultChips] = useState(FALLBACK_SUGGESTIONS);
  const [personalChips, setPersonalChips] = useState([]);

  // Persist mute preference + cancel any in-flight speech the moment user mutes
  useEffect(() => {
    ttsOnRef.current = ttsOn;
    try { localStorage.setItem("mnu_assistant_tts", ttsOn ? "on" : "off"); } catch { /* noop */ }
    if (!ttsOn) {
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    }
  }, [ttsOn]);

  const sessionId = useMemo(() => {
    const k = SESSION_KEY(slug);
    let v = localStorage.getItem(k);
    if (!v) { v = genSessionId(); localStorage.setItem(k, v); }
    return v;
  }, [slug]);

  // Init Web Speech recogniser once
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return; // unsupported (mainly older Safari)
    const r = new SR();
    r.lang = "en-IN";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.continuous = false;
    r.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setInput((prev) => (prev ? `${prev} ${text}` : text));
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    return () => { try { r.abort(); } catch { /* noop */ } };
  }, []);

  // Auto-scroll to latest
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Stop TTS / mic when drawer closes
  useEffect(() => {
    if (!open) {
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      try { recogRef.current?.abort(); } catch { /* noop */ }
      setListening(false);
    }
  }, [open]);

  // Personalised welcome — fires once per drawer-open when authed + no chat yet
  useEffect(() => {
    if (!open || welcomeLoaded || messages.length > 0) return;
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem("crave_token");
      try {
        // Always pull the owner-curated default chips so guests (and anon) get them.
        const cfg = await api.get(`/customer/assistant/config?slug=${encodeURIComponent(slug)}`);
        if (!cancelled && (cfg.data?.default_prompts || []).length) {
          setDefaultChips(cfg.data.default_prompts);
        }
      } catch { /* fall back to FALLBACK_SUGGESTIONS */ }
      if (!token) { if (!cancelled) setWelcomeLoaded(true); return; }
      try {
        const { data } = await api.get(`/customer/assistant/welcome?slug=${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (data?.default_suggestions?.length) setDefaultChips(data.default_suggestions);
        if (data?.personalized_suggestions?.length) setPersonalChips(data.personalized_suggestions);
        if (data?.reply) {
          setMessages([{ role: "assistant", content: data.reply, recs: data.recommended_items || [], personal: true }]);
          speak(data.reply);
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setWelcomeLoaded(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slug]);

  const speak = (text) => {
    if (!ttsOnRef.current) return;
    if (!window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.02; u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch { /* noop */ }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/customer/assistant/chat", {
        slug, session_id: sessionId, message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, recs: data.recommended_items || [] }]);
      speak(data.reply);
    } catch (err) {
      const msg = err?.response?.data?.detail || "The assistant is taking a moment. Try again.";
      setMessages((m) => [...m, { role: "assistant", content: msg, recs: [] }]);
      toast.error("Assistant error");
    } finally { setSending(false); }
  };

  const toggleMic = () => {
    const r = recogRef.current;
    if (!r) { toast.error("Voice not supported on this browser"); return; }
    if (listening) { r.stop(); setListening(false); return; }
    try {
      r.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setWelcomeLoaded(false);
    setPersonalChips([]);
    const fresh = genSessionId();
    localStorage.setItem(SESSION_KEY(slug), fresh);
    // session memo won't update; close+reopen rebuilds context naturally
  };

  return (
    <>
      {/* Pro-only: hide entirely if tenant is not on Pro tier or owner disabled it */}
      {proEnabled && (
      <motion.button
        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={() => setOpen(true)} data-testid="assistant-launch"
        title="Ask the menu — your AI sommelier"
        className="fixed z-40 bottom-24 md:bottom-28 right-4 md:right-6 rounded-full shadow-lg flex items-center gap-2 px-4 py-3"
        style={{ background: "linear-gradient(135deg, #E5A93C, #C68A1C)", color: "#0A0A0C", boxShadow: "0 10px 30px -10px rgba(229,169,60,0.55)" }}
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-semibold">Ask the menu</span>
      </motion.button>
      )}

      <AnimatePresence>
        {proEnabled && open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70"
            onClick={() => setOpen(false)}
            data-testid="assistant-drawer"
          >
            <motion.div
              initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 100 }}
              transition={{ type: "spring", damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl bg-[#0F1115] rounded-t-3xl md:rounded-3xl md:mb-10 border-t md:border border-white/10 overflow-hidden flex flex-col"
              style={{ height: "min(85vh, 720px)" }}
            >
              {/* Header */}
              <div className="px-5 pt-5 pb-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(229,169,60,0.15)", color: "#E5A93C" }}>
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Your AI Sommelier</div>
                    <div className="font-display text-xl">Ask the menu</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setTtsOn((v) => !v)} title={ttsOn ? "Mute voice replies" : "Enable voice replies"} data-testid="assistant-tts-toggle" className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/5" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                    {ttsOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-white/50" />}
                  </button>
                  {messages.length > 0 && (
                    <button onClick={reset} data-testid="assistant-reset" className="text-[11px] uppercase tracking-widest font-mono px-2 py-1 rounded-full hover:bg-white/5" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>New chat</button>
                  )}
                  <button onClick={() => setOpen(false)} data-testid="assistant-close" className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/5" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="assistant-messages">
                {/* Conversation starters — pinned at the top until the guest sends their first message */}
                {!messages.some((m) => m.role === "user") && (
                  <div className="space-y-3" data-testid="assistant-starters">
                    {messages.length === 0 && (
                      <div className="rounded-2xl p-4 border border-[#E5A93C]/20" style={{ background: "rgba(229,169,60,0.06)" }}>
                        <div className="font-display text-lg text-white">Hello — I&apos;m your in-menu sommelier.</div>
                        <div className="text-sm text-white/70 mt-1">Tell me your mood, dietary needs, group size or occasion. Tap the mic and just talk.</div>
                      </div>
                    )}
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                      {personalChips.length ? "Made for you" : "Try asking"}
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="assistant-chip-row">
                      {(personalChips.length ? personalChips : defaultChips).map((s, i) => (
                        <button key={`p-${i}-${s}`} onClick={() => send(s)} data-testid={`assistant-suggest-${i}`} className="text-xs px-3 py-1.5 rounded-full border hover:bg-white/5"
                          style={{
                            borderColor: personalChips.length ? "#E5A93C" : "rgba(255,255,255,0.1)",
                            color: personalChips.length ? "#E5A93C" : "rgba(255,255,255,0.8)",
                            background: personalChips.length ? "rgba(229,169,60,0.08)" : "transparent",
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    {/* Show generic chips as a secondary row when we have personalised ones */}
                    {personalChips.length > 0 && defaultChips.length > 0 && (
                      <>
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mt-3">Or try a classic</div>
                        <div className="flex flex-wrap gap-2">
                          {defaultChips.map((s, i) => (
                            <button key={`d-${i}-${s}`} onClick={() => send(s)} data-testid={`assistant-default-suggest-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 text-white/70">
                              {s}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#E5A93C] text-black" : "bg-white/5 text-white border border-white/10"}`} data-testid={`msg-${m.role}-${i}`}>
                      <div className="whitespace-pre-wrap">{m.content}</div>
                      {m.role === "assistant" && m.recs && m.recs.length > 0 && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`msg-recs-${i}`}>
                          {m.recs.map((it) => (
                            <div key={it.id} className="rounded-xl overflow-hidden border border-white/10 bg-[#121418] flex flex-col" data-testid={`rec-${it.id}`}>
                              <img src={dishImg(it)} onError={onDishImgError} alt={it.name} className="w-full h-20 object-cover" />
                              <div className="p-2 flex-1 flex flex-col">
                                <div className="font-display text-sm leading-snug truncate">{it.name}</div>
                                <div className="text-[10px] font-mono text-white/50 mb-1">₹{Math.round(it.price)}</div>
                                <button
                                  onClick={() => { onAddItem?.(it); toast.success(`${it.name} added`); }}
                                  data-testid={`rec-add-${it.id}`}
                                  className="mt-auto text-[11px] font-semibold rounded-full py-1 inline-flex items-center justify-center gap-1"
                                  style={{ background: "#E5A93C", color: "#0A0A0C" }}
                                >
                                  <Plus className="w-3 h-3" /> Add
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2 text-sm text-white/70 flex items-center gap-2" data-testid="assistant-typing">
                      <Sparkles className="w-3.5 h-3.5 text-[#E5A93C] animate-pulse" />
                      Pairing your meal…
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="px-3 py-3 border-t border-white/10">
                <form
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                  className="flex items-center gap-2"
                  data-testid="assistant-composer"
                >
                  <button
                    type="button" onClick={toggleMic}
                    data-testid="assistant-mic"
                    title={listening ? "Stop listening" : "Speak"}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${listening ? "bg-red-500 text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
                    style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                  >
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={listening ? "Listening…" : "Tell me what you’re craving"}
                    data-testid="assistant-input"
                    className="flex-1 bg-[#121418] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#E5A93C] focus:ring-1 focus:ring-[#E5A93C] focus:outline-none"
                  />
                  <button
                    type="submit" disabled={sending || !input.trim()}
                    data-testid="assistant-send"
                    className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40"
                    style={{ background: "#E5A93C", color: "#0A0A0C" }}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mt-2 text-center">
                  Powered by MnuVerse Sommelier · Suggestions only — please confirm allergens with staff.
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
